import { format } from "date-fns";
import { Star } from "lucide-react";
import {
  ScatterChart,
  Scatter,
  BarChart,
  Bar,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  TooltipProps
} from "recharts";
import { VersionMetrics } from "@/types/application";
import { Card, CardContent } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { Badge } from "@/components/ui/badge";

interface VersionTimelineChartProps {
  metrics: VersionMetrics[];
}

const CustomDot = (props: any) => {
  const { cx, cy, payload } = props;
  
  if (payload.isMilestone) {
    return (
      <Star 
        x={cx - 6} 
        y={cy - 6} 
        width={12} 
        height={12} 
        fill="hsl(var(--primary))"
        className="animate-pulse"
      />
    );
  }
  
  return (
    <circle 
      cx={cx} 
      cy={cy} 
      r={4} 
      fill={payload.isActive ? "hsl(var(--chart-2))" : "hsl(var(--muted-foreground))"}
      className="cursor-pointer hover:r-6 transition-all"
    />
  );
};

const CustomTooltip = ({ active, payload }: TooltipProps<any, any>) => {
  if (!active || !payload || !payload.length) return null;

  const data = payload[0].payload;
  
  return (
    <Card className="border-border shadow-lg">
      <CardContent className="p-3">
        <div className="space-y-2">
          <div className="font-semibold text-foreground">Version {data.version}</div>
          <div className="text-xs text-muted-foreground">{data.date}</div>
          <Separator />
          <div className="space-y-1 text-xs">
            <div className="flex justify-between gap-4">
              <span className="text-chart-2">Keys Added:</span>
              <span className="font-medium">{data.keysAdded}</span>
            </div>
            <div className="flex justify-between gap-4">
              <span className="text-chart-3">Keys Modified:</span>
              <span className="font-medium">{data.keysModified}</span>
            </div>
            <div className="flex justify-between gap-4">
              <span className="text-chart-1">Keys Removed:</span>
              <span className="font-medium">{data.keysRemoved}</span>
            </div>
            <div className="flex justify-between gap-4">
              <span className="text-foreground">Total Keys:</span>
              <span className="font-medium">{data.totalKeys}</span>
            </div>
            <div className="flex justify-between gap-4">
              <span className="text-foreground">Customers:</span>
              <span className="font-medium">{data.customers}</span>
            </div>
            {data.isMilestone && (
              <Badge variant="secondary" className="mt-2">
                <Star className="h-3 w-3 mr-1" />
                Milestone
              </Badge>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export const VersionTimelineChart = ({ metrics }: VersionTimelineChartProps) => {
  const chartData = metrics.map(m => ({
    date: format(new Date(m.date), 'MMM dd'),
    fullDate: format(new Date(m.date), 'MMM dd, yyyy'),
    version: m.versionNumber,
    keysAdded: m.keysAdded,
    keysModified: m.keysModified,
    keysRemoved: m.keysRemoved,
    totalKeys: m.totalKeys,
    customers: m.totalCustomers,
    isActive: m.isActive ? 1 : 0,
    isMilestone: m.isMilestone
  }));

  return (
    <div className="space-y-6">
      {/* Timeline Scatter Plot */}
      <div>
        <h4 className="text-sm font-medium mb-3 text-foreground">Version Timeline</h4>
        <ResponsiveContainer width="100%" height={150}>
          <ScatterChart margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
            <XAxis 
              dataKey="date" 
              stroke="hsl(var(--muted-foreground))"
              tick={{ fill: 'hsl(var(--muted-foreground))' }}
            />
            <YAxis 
              dataKey="version" 
              stroke="hsl(var(--muted-foreground))"
              tick={{ fill: 'hsl(var(--muted-foreground))' }}
              label={{ value: 'Version', angle: -90, position: 'insideLeft', fill: 'hsl(var(--muted-foreground))' }}
            />
            <Tooltip content={<CustomTooltip />} />
            <Scatter 
              data={chartData} 
              fill="hsl(var(--primary))"
              shape={<CustomDot />}
            />
          </ScatterChart>
        </ResponsiveContainer>
      </div>

      {/* Change Activity Bar Chart */}
      <div>
        <h4 className="text-sm font-medium mb-3 text-foreground">Change Activity</h4>
        <ResponsiveContainer width="100%" height={200}>
          <BarChart data={chartData} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
            <XAxis 
              dataKey="date" 
              stroke="hsl(var(--muted-foreground))"
              tick={{ fill: 'hsl(var(--muted-foreground))' }}
            />
            <YAxis 
              stroke="hsl(var(--muted-foreground))"
              tick={{ fill: 'hsl(var(--muted-foreground))' }}
            />
            <Tooltip content={<CustomTooltip />} />
            <Legend wrapperStyle={{ color: 'hsl(var(--foreground))' }} />
            <Bar dataKey="keysAdded" stackId="a" fill="hsl(var(--chart-2))" name="Keys Added" />
            <Bar dataKey="keysModified" stackId="a" fill="hsl(var(--chart-3))" name="Keys Modified" />
            <Bar dataKey="keysRemoved" stackId="a" fill="hsl(var(--chart-1))" name="Keys Removed" />
          </BarChart>
        </ResponsiveContainer>
      </div>

      {/* Total Keys Trend Line Chart */}
      <div>
        <h4 className="text-sm font-medium mb-3 text-foreground">Configuration Size Over Time</h4>
        <ResponsiveContainer width="100%" height={150}>
          <LineChart data={chartData} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
            <XAxis 
              dataKey="date" 
              stroke="hsl(var(--muted-foreground))"
              tick={{ fill: 'hsl(var(--muted-foreground))' }}
            />
            <YAxis 
              stroke="hsl(var(--muted-foreground))"
              tick={{ fill: 'hsl(var(--muted-foreground))' }}
            />
            <Tooltip content={<CustomTooltip />} />
            <Legend wrapperStyle={{ color: 'hsl(var(--foreground))' }} />
            <Line 
              type="monotone" 
              dataKey="totalKeys" 
              stroke="hsl(var(--chart-4))" 
              name="Total Keys"
              strokeWidth={2}
              dot={{ fill: 'hsl(var(--chart-4))' }}
            />
            <Line 
              type="monotone" 
              dataKey="customers" 
              stroke="hsl(var(--chart-5))" 
              name="Customers"
              strokeWidth={2}
              dot={{ fill: 'hsl(var(--chart-5))' }}
            />
          </LineChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};
